"use client";

import * as React from "react";
import { Callsign } from "@modernatc/ui/callsign";

interface GatedRevealProps {
  slug: string;
  excerpt: string;
  signupUrl: string;
  loginUrl: string;
}

/**
 * Client-side reveal for gated posts.
 *
 * The server renders only the teaser + signup CTA below, so the gated body is
 * never baked into the cached/SSR HTML (crawlers index the teaser, anonymous
 * readers hit the wall). On mount we ask the cookie-aware
 * `/blog/[slug]/body` route handler for the full body — it returns it only
 * when the reader is signed in (the SECURITY DEFINER RPC enforces this) — and
 * swap it in. This keeps the page statically cacheable while still gating per
 * reader.
 */
export function GatedReveal({
  slug,
  excerpt,
  signupUrl,
  loginUrl,
}: GatedRevealProps) {
  const [bodyHtml, setBodyHtml] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;
    fetch(`/blog/${encodeURIComponent(slug)}/body`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { html?: string } | null) => {
        if (active && data?.html) setBodyHtml(data.html);
      })
      .catch(() => {
        /* leave the teaser in place */
      });
    return () => {
      active = false;
    };
  }, [slug]);

  if (bodyHtml) {
    return (
      <article
        className="post-prose mt-12 text-pretty text-[var(--color-ink-dim)]"
        dangerouslySetInnerHTML={{ __html: bodyHtml }}
      />
    );
  }

  return (
    <>
      <article className="post-prose mt-12 text-pretty text-[var(--color-ink-dim)]">
        <p>{excerpt}</p>
      </article>

      <aside className="mt-10 rounded-[var(--radius-md)] border border-[var(--color-amber)]/40 bg-[var(--color-amber-glow)] p-6 text-center">
        <Callsign className="text-[10px] text-[var(--color-amber)]">
          ▸ Free members preview
        </Callsign>
        <p className="mx-auto mt-2 max-w-md text-pretty text-[var(--color-ink)]">
          The rest of this guide is free — create a ModernATC account to keep
          reading. No card required.
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <a
            href={signupUrl}
            className="inline-flex items-center gap-1.5 rounded-[var(--radius)] bg-[var(--color-amber)] px-4 py-2 text-sm font-medium text-[var(--color-panel-deep)] hover:bg-[var(--color-amber-bright)]"
          >
            Create free account
          </a>
          <a
            href={loginUrl}
            className="inline-flex items-center gap-1.5 rounded-[var(--radius)] border border-[var(--color-panel-edge)] px-4 py-2 text-sm text-[var(--color-ink-dim)] hover:text-[var(--color-ink)]"
          >
            Sign in
          </a>
        </div>
      </aside>
    </>
  );
}
